import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useAppSelector, useAppDispatch } from '../../ducks/hooks';
import { setAppConfig, clearAppConfig } from '../../ducks/app-config/duck-app-config';
import { setParticipationDetails } from '../../ducks/participation-details/duck-participation-details';
import { setFeatureFlags } from '../../ducks/feature-flags/duck-feature-flags';
import {
  setDefaultFilters,
  clearFilters,
  setSelectedPaymentPartition,
  setSelectedPaymentPartitionGroup,
  addAvailablePaymentPartitions,
} from '../../ducks/filters/duck-filters';
import { TCoreParticipation } from '../../ducks/api-ducks/participation-details-duck/types-participation-details';
import { useGetParticipantDetailsQuery } from '../../ducks/api-ducks/participation-details-duck/api-duck-participation-details';
import { useGetAppConfigQuery } from '../../ducks/api-ducks/app-config-duck/api-duck-app-config';
import { useGetFeatureFlagsQuery } from '../../ducks/api-ducks/feature-flags-duck/api-duck-feature-flags';
import { UnknownUser } from '../../shared-components/component-unknown-user';
import { LoadingPlaceholder } from '../../shared-components/hoc-loading-placeholder';
import { useGetPaymentPartitionsQuery } from '../../ducks/api-ducks/earnings-duck/api-duck-earnings';
import {
  getDefaultPartition,
  getDefaultPartitionGroup,
  getLatestConfiguredParticipation,
  getPaymentPartitionByParticipationPartition,
} from '../../ducks/filters/utils-filters';
import { addError, EBannerType } from '../../ducks/banner-messages/duck-banner-messages';
import AppEnvironment from '../../environment/app-environment';
import { useMEMTranslation } from '../../utils/text-utils';

export const SelexpProvider = (props: PropsWithChildren) => {
  const { children } = props;
  const { t } = useMEMTranslation();
  const [isUnknownUser, setIsUnknownUser] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const dispatch: any = useAppDispatch();
  const appConfigErrorLogged = useRef<boolean>(false);
  const { impersonatedUser } = useAppSelector((state) => state.impersonation, shallowEqual);
  const impersonationState = useAppSelector((state) => state.impersonation);
  const appConfig = useAppSelector((state) => state.appConfig);
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const defaultFiscalYearId = useAppSelector((state) => state.appConfig.defaultFiscalYearId);

  const { data: dataAppConfig, isFetching: loadingAppConfig, error: errorAppConfig } = useGetAppConfigQuery(impersonatedUser);

  const { data: dataFeatureFlags, isFetching: loadingFeatureFlags } = useGetFeatureFlagsQuery(impersonatedUser);

  const {
    data: dataParticipationDetails,
    isFetching: loadingParticipationDetails,
    error: errorParticipationDetails,
  } = useGetParticipantDetailsQuery(impersonatedUser);

  const fiscalYearId = useMemo(() => {
    let defaultParticipation: TCoreParticipation | null | undefined;

    if (participationDetails) {
      defaultParticipation = getLatestConfiguredParticipation(appConfig, participationDetails);
    }
    return `${defaultParticipation?.fiscalYearId || defaultFiscalYearId}`;
  }, [appConfig, defaultFiscalYearId, participationDetails]);

  const { data: dataPaymentPartitions } = useGetPaymentPartitionsQuery(
    fiscalYearId && fiscalYearId.length > 1 && participationDetails?.personnelNumber
      ? {
          fiscalYearId: `${fiscalYearId}`,
          personnelNumber: participationDetails.personnelNumber,
        }
      : skipToken
  );

  useEffect(() => {
    if (errorParticipationDetails) {
      dispatch(
        addError({
          message: t('errorLoadingTryAgain'),
          bannerType: EBannerType.PageError,
        })
      );
      dispatch(setParticipationDetails(null));
    } else if (!(dataParticipationDetails === undefined)) {
      dispatch(setParticipationDetails(dataParticipationDetails));
    } else {
      dispatch(setParticipationDetails(null));
    }
  }, [dispatch, dataParticipationDetails, errorParticipationDetails, t]);

  useEffect(() => {
    if (errorAppConfig) {
      dispatch(clearAppConfig());
      if (!appConfigErrorLogged.current) {
        appConfigErrorLogged.current = true;
        dispatch(
          addError({
            message: t('errorLoadingTryAgain'),
            bannerType: EBannerType.PageError,
          })
        );
      }
    } else if (dataAppConfig) {
      dispatch(setAppConfig(dataAppConfig));
      appConfigErrorLogged.current = false;
    }
  }, [dataAppConfig, dispatch, errorAppConfig, t]);

  useEffect(() => {
    if (dataFeatureFlags) {
      dispatch(setFeatureFlags(dataFeatureFlags));
    }
  }, [dataFeatureFlags, dispatch]);

  // Effect to set default filters whenever the appConfig or participationDetails change
  useEffect(() => {
    if (!loadingAppConfig && !loadingParticipationDetails && appConfig && participationDetails) {
      const isImpersonating = impersonationState?.isImpersonating || false;
      dispatch(setDefaultFilters({ appConfig, participationDetails, isImpersonating }));
    } else {
      dispatch(clearFilters());
    }
  }, [appConfig, participationDetails, dispatch, loadingAppConfig, loadingParticipationDetails, impersonationState?.isImpersonating]);

  // Effect to fetch the default payment partitions for earnings tab filters.
  useEffect(() => {
    if (
      dataPaymentPartitions &&
      (dataPaymentPartitions[0].personnelNumber === participationDetails?.personnelNumber ||
        participationDetails?.personnelNumber === AppEnvironment.demoUserParticipationId)
    ) {
      // We memoize the available partitions for this fiscal year
      dispatch(
        addAvailablePaymentPartitions({
          partitionGroup: `${fiscalYearId}`,
          partitions: dataPaymentPartitions,
        })
      );
      // We set the default PaymentPartitionGroup and PaymentPartition
      dispatch(
        setSelectedPaymentPartitionGroup({
          partitionGroup: `${fiscalYearId}`,
          partitions: dataPaymentPartitions,
        })
      );
      // We extract the default participation period to use as default PaymentPartition
      if (participationDetails) {
        const defaultParticipation = getLatestConfiguredParticipation(appConfig, participationDetails);
        const selectedParticipationPartitionGroup = getDefaultPartitionGroup(
          defaultParticipation,
          defaultParticipation?.availablePartitions || [],
          Number(fiscalYearId)
        );
        const selectedParticipationPartition = getDefaultPartition(selectedParticipationPartitionGroup?.partitions);
        dispatch(setSelectedPaymentPartition(getPaymentPartitionByParticipationPartition(dataPaymentPartitions, selectedParticipationPartition)));
      }
    }
  }, [appConfig, dataPaymentPartitions, dispatch, fiscalYearId, participationDetails]);

  // Effect to wait for async calls return
  useEffect(() => {
    if (!(loadingAppConfig || loadingFeatureFlags || loadingParticipationDetails)) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [loadingAppConfig, loadingFeatureFlags, loadingParticipationDetails]);

  // Effect that catches the scenario of an unknown user (no participant details but and no businessPersona on AppConfig)
  useEffect(() => {
    if (isLoading) {
      return;
    }
    // If results came back and appConfig came empty, then its unknown user
    if (!appConfig) {
      setIsUnknownUser(true);
      // If isBusinessPersona is not true, but there are no participationDetails, then its unknown user
    } else if (!appConfig.isBusinessPersona && !dataParticipationDetails) {
      setIsUnknownUser(true);
    }
  }, [isLoading, dataParticipationDetails, appConfig]);

  return (
    <LoadingPlaceholder isLoading={isLoading} includeStandaloneHeader>
      {isUnknownUser && <UnknownUser />}
      {/* The following logic was used during flighting rollout. The code is left here commented, in case we need similar functionality in the future. */}
      {/* {!isUserFlighted && !isUnknownUser && <NotFlightedUser />} */}
      {/* {(!isUnknownUser && isUserFlighted) && children as React.ReactElement} */}
      {!isUnknownUser && (children as React.ReactElement<any>)}
    </LoadingPlaceholder>
  );
};
